import { DestroyRef, inject, Injectable, isDevMode } from '@angular/core';
import {
  catchError,
  combineLatestWith,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  skip,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { EMPTY, fromEvent, merge, Observable, of, OperatorFunction, share, Subject, timer } from 'rxjs';
import { GameServerUpdateMessage, Message, TeamSpeakUpdateMessage, Topic } from '../../model/model';
import { TeamSpeakServer as TeamSpeakServer } from '../../model/teamspeak';
import { SocketService } from './socket.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { GameServer } from '../../model/gameserver';
import { VisibilityService } from '../../visibility.service';
import { OnlineService } from '../../online.service';
import { BrowserContext } from '../../browser-context.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly ctx = inject(BrowserContext);
  private readonly httpClient = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly socketService = inject(SocketService);
  private readonly onlineService = inject(OnlineService);

  public readonly serverUpdates$!: Observable<GameServer>;
  public readonly teamSpeakUpdates$!: Observable<TeamSpeakServer>;

  private readonly visibilityService = inject(VisibilityService);

  constructor() {
    if (this.ctx.isBrowser) {
      // i.e. page reload causes websocket to close but does not destroy the service/component causing the fallback to be activated
      const beforeUnload$ = fromEvent(this.ctx.window, 'beforeunload').pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => isDevMode() && console.log('RX BeforeUnload')),
        shareReplay(1)
      );

      const shouldUpdate$ = of(true).pipe(
        combineLatestWith(
          this.onlineService.status$.pipe(map(value => value === 'online')),
          this.visibleOrHiddenForAtLeast(environment.inactivity)
        ),
        map(value => value.every(value => value === true)),
        tap(value => isDevMode() && console.log('RX Should Update: ', value)),
        shareReplay(1)
      );

      const pullTimer$ = timer(0, environment.refreshInterval).pipe(share());

      const tsSocketUpdates$ = this.socketSource('teamspeak', x => (x as unknown as TeamSpeakUpdateMessage).server);
      const tsHttpUpdates$ = pullTimer$.pipe(this.fetchHttp<TeamSpeakServer>('teamspeak'));
      const tsUpdates$ = this.withFallback(tsSocketUpdates$, tsHttpUpdates$);
      this.teamSpeakUpdates$ = shouldUpdate$.pipe(
        switchMap(value => (value ? tsUpdates$ : of())),
        takeUntil(beforeUnload$),
        takeUntilDestroyed(this.destroyRef)
      );

      const gsSocketUpdates$ = this.socketSource('gameserver', x => (x as unknown as GameServerUpdateMessage).server);
      const gsHttpUpdates$ = pullTimer$.pipe(this.fetchHttp<GameServer[]>('gameserver')).pipe(mergeMap(value => value));
      const gsUpdates$ = this.withFallback(gsSocketUpdates$, gsHttpUpdates$);
      this.serverUpdates$ = shouldUpdate$.pipe(
        switchMap(value => (value ? gsUpdates$ : of())),
        takeUntil(beforeUnload$),
        takeUntilDestroyed(this.destroyRef)
      );
    } else {
      this.serverUpdates$ = EMPTY;
      this.teamSpeakUpdates$ = EMPTY;
    }
  }

  private visibleOrHiddenForAtLeast(milliseconds: number) {
    const cancelDisconnect$ = new Subject<void>();
    return this.visibilityService.visibility$.pipe(
      switchMap(state => {
        if (state === 'visible') {
          cancelDisconnect$.next(); // Cancel any pending disconnect
          return of(true);
        } else {
          return timer(milliseconds).pipe(
            takeUntil(cancelDisconnect$),
            switchMap(() => of(false)) // Unsubscribe from WebSocket
          );
        }
      }),
      distinctUntilChanged()
    );
  }

  private fetchHttp<T>(topic: Topic): OperatorFunction<unknown, T> {
    return source => {
      return source
        .pipe(
          concatMap(() => {
            return this.httpClient.get<T>(`${environment.endpoints.api}/${topic}`, { responseType: 'json' }).pipe(
              catchError(() => of()),
              tap({
                error: x => console.error('Update via GET failed', x),
              })
            );
          })
        )
        .pipe(tap(x => isDevMode() && console.log('Http Updates:', x)));
    };
  }

  private withFallback<T>(primary: Observable<T>, fallback: Observable<T>) {
    const onlineStatus$ = this.socketService.onlineStatus$;

    // Assume WS online for Xms before switching to HTTP
    const timeout = timer(500).pipe(
      map(() => false),
      takeUntil(onlineStatus$.pipe(skip(1))),
      takeUntil(onlineStatus$.pipe(filter(value => value === true)))
    );
    const disableFallback = merge(onlineStatus$.pipe(skip(1)), timeout).pipe(startWith(true), distinctUntilChanged());
    const toggledFallback = disableFallback.pipe(
      tap(value => isDevMode() && console.log('RX Mechanism', value ? 'WS' : 'HTTP')),
      switchMap(value => (value ? of() : fallback))
    );
    return merge(primary, toggledFallback).pipe(takeUntilDestroyed(this.destroyRef), share());
  }

  private socketSource<T>(topic: Topic, mapper: (x: Message) => T): Observable<T> {
    return this.socketService.getUpdateStream(topic).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(value => mapper(value)),
      tap(x => isDevMode() && console.log('WS Update:', topic, x))
    );
  }
}
